/**
 * theme.js — Tiziri Coffee Shopify Theme
 * Ported from the Next.js / React site.
 *
 * Modules:
 *  1. Brand Wordmark animation   (from BrandWordmark.tsx)
 *  2. Cart (AJAX)                (from CartContext.tsx)
 *  3. Countdown timer            (from Countdown.tsx)
 *  4. Ceramics Lightbox          (from CeramicsLightbox.tsx)
 *  5. Quantity selectors         (from CoffeeDropClient.tsx)
 *  6. Waitlist forms             (from WaitlistForm.tsx)
 *  7. Ceramics add-to-cart       (from CollectionClient.tsx)
 */

(function () {
  'use strict';

  // ─── 1. BRAND WORDMARK ANIMATION ──────────────────────────────────────────
  // Ported from BrandWordmark.tsx useEffect
  function initWordmark() {
    // On mobile the floating wordmark is hidden via CSS; skip animation entirely.
    if (window.innerWidth <= 768) return;

    var brand = document.getElementById('brand-wordmark');
    if (!brand) return;

    // Both bars are now position:fixed, so we measure their actual heights
    // so wordmark positions stay correct regardless of font/rem changes.
    var announceBar = document.querySelector('.announcement-bar');
    var ANNOUNCE_H  = announceBar ? announceBar.offsetHeight : 40;
    var NAV_H       = 68;
    var HERO_PAD    = 44;
    var IMG_RATIO   = 375 / 151;

    // START: large wordmark sitting HERO_PAD below the bottom of the nav bar
    var START_TOP   = ANNOUNCE_H + NAV_H + HERO_PAD;

    var START_WIDTH = Math.min(512, Math.max(256, 0.432 * window.innerWidth));
    var END_WIDTH   = 84;

    // END: small wordmark vertically centred inside the fixed nav bar
    var END_TOP     = ANNOUNCE_H + Math.round((NAV_H - END_WIDTH / IMG_RATIO) / 2);
    var END_LEFT    = 48;

    var isHomepage  = document.body.dataset.template === 'index';

    // ── Non-homepage: immediately lock to the small nav position, no animation
    if (!isHomepage) {
      brand.style.transition = 'none';
      brand.style.width      = END_WIDTH + 'px';
      brand.style.left       = END_LEFT  + 'px';
      brand.style.top        = END_TOP   + 'px';
      brand.style.right      = 'auto';
      return; // no scroll listener needed
    }

    // ── Homepage: full scroll-driven animation ─────────────────────────────
    var startLeft = 0;
    var measured  = false;

    function measureWordmark() {
      brand.style.transition = 'none';
      brand.style.width      = START_WIDTH + 'px';
      brand.style.right      = '3rem';
      brand.style.left       = 'auto';
      brand.style.top        = START_TOP + 'px';

      requestAnimationFrame(function () {
        var rect  = brand.getBoundingClientRect();
        startLeft = rect.left;
        brand.style.right = 'auto';
        brand.style.left  = startLeft + 'px';

        requestAnimationFrame(function () {
          brand.style.transition =
            'left 0.6s cubic-bezier(0.4,0,0.2,1),' +
            'top 0.6s cubic-bezier(0.4,0,0.2,1),' +
            'width 0.6s cubic-bezier(0.4,0,0.2,1)';
          measured = true;
          applyScrollState();
        });
      });
    }

    function applyScrollState() {
      if (!measured) return;
      var hero      = document.querySelector('.hero');
      // Trigger the collapse once user has scrolled ~35% of the hero height
      var threshold = (hero ? hero.offsetHeight : window.innerHeight) * 0.35;
      var scrolled  = window.scrollY > threshold;

      if (scrolled) {
        brand.style.left  = END_LEFT + 'px';
        brand.style.top   = END_TOP  + 'px';
        brand.style.width = END_WIDTH + 'px';
      } else {
        brand.style.left  = startLeft + 'px';
        brand.style.top   = START_TOP + 'px';
        brand.style.width = START_WIDTH + 'px';
      }
    }

    function handleResize() {
      if (window.innerWidth <= 768) return;
      ANNOUNCE_H  = announceBar ? announceBar.offsetHeight : 40;
      START_TOP   = ANNOUNCE_H + NAV_H + HERO_PAD;
      END_TOP     = ANNOUNCE_H + Math.round((NAV_H - END_WIDTH / IMG_RATIO) / 2);
      START_WIDTH = Math.min(512, Math.max(256, 0.432 * window.innerWidth));
      measured    = false;
      measureWordmark();
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureWordmark);
    } else {
      setTimeout(measureWordmark, 300);
    }

    window.addEventListener('scroll', applyScrollState, { passive: true });
    window.addEventListener('resize', handleResize);
  }

  // ─── 2. CART (AJAX) ───────────────────────────────────────────────────────
  // Replaces CartContext — uses Shopify's Cart AJAX API
  function initCart() {
    updateCartCount();

    // Intercept homepage "add to cart" form (product-form--homepage)
    var homepageForms = document.querySelectorAll('.product-form--homepage');
    homepageForms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var id  = form.querySelector('[name="id"]').value;
        var qty = parseInt(form.querySelector('[name="quantity"]').value || '1', 10);
        addToCart(id, qty, function () {
          window.location.href = '/cart';
        });
      });
    });

    // Intercept product-form (product page) for AJAX add
    var productForm = document.getElementById('product-form');
    if (productForm) {
      productForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var id  = productForm.querySelector('[name="id"]').value;
        var qty = parseInt(productForm.querySelector('[name="quantity"]').value || '1', 10);
        var btn = productForm.querySelector('[type="submit"]');
        var originalText = btn ? btn.textContent : '';

        addToCart(id, qty, function () {
          if (btn) {
            btn.textContent = 'Added to cart ✓';
            setTimeout(function () { btn.textContent = originalText; }, 2000);
          }
          updateCartCount();
        });
      });
    }

    // Ceramics add-to-cart buttons (on collection page)
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.ceramics-add-btn');
      if (!btn) return;
      e.preventDefault();
      var variantId = btn.dataset.variantId;
      var name      = btn.dataset.name;
      var orig      = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      addToCart(variantId, 1, function () {
        btn.textContent = 'Added ✓';
        updateCartCount();
        setTimeout(function () {
          btn.textContent = orig;
          btn.disabled = false;
        }, 2000);
      });
    });

    // Cart page: quantity selector changes → update via cart/change.js
    var cartForm = document.getElementById('cart-form');
    if (cartForm) {
      initCartPage(cartForm);
    }
  }

  function addToCart(variantId, quantity, callback) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      updateCartCount();
      if (callback) callback();
    })
    .catch(function (err) { console.error('cart/add error', err); });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var badges = document.querySelectorAll('.cart-count-badge');
        badges.forEach(function (badge) {
          badge.textContent = cart.item_count > 9 ? '9+' : cart.item_count;
          badge.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
        });
      });
  }

  function initCartPage(form) {
    // +/- buttons update the hidden number input, form submits to Shopify
    form.querySelectorAll('.quantity-selector').forEach(function (selector) {
      var input  = selector.querySelector('.qty-input');
      var minus  = selector.querySelector('.qty-btn--minus');
      var plus   = selector.querySelector('.qty-btn--plus');
      if (!input) return;

      if (minus) minus.addEventListener('click', function () {
        var val = parseInt(input.value, 10);
        if (val > 0) { input.value = val - 1; }
      });
      if (plus) plus.addEventListener('click', function () {
        var val = parseInt(input.value, 10);
        input.value = val + 1;
      });
    });
  }

  // ─── 3. COUNTDOWN TIMER ───────────────────────────────────────────────────
  function initCountdown() {
    document.querySelectorAll('[data-launch]').forEach(function (el) {
      var launchDate = el.dataset.launch;
      if (!launchDate) return;
      var targetTime = new Date(launchDate).getTime();
      if (isNaN(targetTime)) return;

      el.innerHTML =
        '<p class="countdown-label">Next drop in</p>' +
        '<div class="countdown-units">' +
          '<div class="countdown-unit"><span class="countdown-val" data-unit="d">00</span><span class="countdown-key">Days</span></div>' +
          '<div class="countdown-unit"><span class="countdown-val" data-unit="h">00</span><span class="countdown-key">Hours</span></div>' +
          '<div class="countdown-unit"><span class="countdown-val" data-unit="m">00</span><span class="countdown-key">Min</span></div>' +
          '<div class="countdown-unit"><span class="countdown-val" data-unit="s">00</span><span class="countdown-key">Sec</span></div>' +
        '</div>';

      function tick() {
        var diff    = Math.max(0, targetTime - Date.now());
        var days    = Math.floor(diff / 86400000);
        var hours   = Math.floor((diff % 86400000) / 3600000);
        var minutes = Math.floor((diff % 3600000) / 60000);
        var seconds = Math.floor((diff % 60000) / 1000);
        var pad     = function (n) { return String(n).padStart(2, '0'); };
        el.querySelector('[data-unit="d"]').textContent = pad(days);
        el.querySelector('[data-unit="h"]').textContent = pad(hours);
        el.querySelector('[data-unit="m"]').textContent = pad(minutes);
        el.querySelector('[data-unit="s"]').textContent = pad(seconds);
        if (diff > 0) setTimeout(tick, 1000);
      }

      tick();
    });
  }

  // ─── 4. CERAMICS LIGHTBOX ─────────────────────────────────────────────────
  // Ported from CeramicsLightbox.tsx
  function initLightbox() {
    var lightbox  = document.getElementById('lightbox');
    if (!lightbox) return;

    var closeBtn  = lightbox.querySelector('.lightbox-close');
    var prevBtn   = lightbox.querySelector('.lightbox-prev');
    var nextBtn   = lightbox.querySelector('.lightbox-next');
    var img       = lightbox.querySelector('.lightbox-img');
    var images    = [];
    var currentIdx = 0;

    function open(imgs, idx) {
      images = imgs;
      currentIdx = idx;
      showImage();
      lightbox.setAttribute('aria-hidden', 'false');
      lightbox.classList.add('lightbox--open');
      document.body.style.overflow = 'hidden';
      closeBtn.focus();
    }

    function close() {
      lightbox.setAttribute('aria-hidden', 'true');
      lightbox.classList.remove('lightbox--open');
      document.body.style.overflow = '';
    }

    function showImage() {
      if (!images[currentIdx]) return;
      img.src = images[currentIdx];
      img.alt = 'Image ' + (currentIdx + 1) + ' of ' + images.length;
      prevBtn.style.display = images.length > 1 ? '' : 'none';
      nextBtn.style.display = images.length > 1 ? '' : 'none';
    }

    function prev() { currentIdx = (currentIdx - 1 + images.length) % images.length; showImage(); }
    function next() { currentIdx = (currentIdx + 1) % images.length; showImage(); }

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn)  prevBtn.addEventListener('click', prev);
    if (nextBtn)  nextBtn.addEventListener('click', next);

    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) close();
    });

    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('lightbox--open')) return;
      if (e.key === 'Escape')     close();
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
    });

    // Attach triggers to ceramics items
    document.querySelectorAll('.ceramics-item__trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var article    = trigger.closest('[data-images]');
        if (!article) return;
        var rawImages  = article.dataset.images || '';
        var imageList  = rawImages.split('|').filter(Boolean);
        if (imageList.length) open(imageList, 0);
      });
    });
  }

  // ─── 5. QUANTITY SELECTORS (product page) ─────────────────────────────────
  function initQuantitySelectors() {
    document.querySelectorAll('.quantity-selector').forEach(function (selector) {
      var input = selector.querySelector('.qty-input');
      var minus = selector.querySelector('.qty-btn--minus');
      var plus  = selector.querySelector('.qty-btn--plus');
      if (!input) return;

      // Update total price display if present
      var priceEl = document.querySelector('.product-price');

      if (minus) minus.addEventListener('click', function () {
        var val = parseInt(input.value, 10);
        var min = parseInt(input.min || '1', 10);
        if (val > min) {
          input.value = val - 1;
          updatePriceDisplay(input.value, priceEl);
        }
      });

      if (plus) plus.addEventListener('click', function () {
        var val = parseInt(input.value, 10);
        var max = parseInt(input.max || '999', 10);
        if (val < max) {
          input.value = val + 1;
          updatePriceDisplay(input.value, priceEl);
        }
      });
    });
  }

  function updatePriceDisplay(qty, priceEl) {
    if (!priceEl) return;
    var unitPrice = parseFloat(priceEl.dataset.unitPrice || '0');
    if (!unitPrice) return;
    var total = (unitPrice * parseInt(qty, 10) / 100).toFixed(2);
    priceEl.textContent = '$' + total;
  }

  // ─── 6. CUSTOMER / NEWSLETTER FORMS (AJAX, no full page reload) ───────────
  // Real {% form 'customer' %} in Liquid supplies Shopify hidden fields. We POST
  // url-encoded via fetch. Do not use stopImmediatePropagation — Shopify injects
  // extra hidden fields / tokens in submit handlers; blocking them causes 400.
  // Bubble phase + setTimeout(0): preventDefault immediately, then serialize after
  // Shopify's listeners and microtasks so the body matches a native submit.
  function customerFormToUrlParams(formEl) {
    var params = new URLSearchParams();
    for (var i = 0; i < formEl.elements.length; i++) {
      var el = formEl.elements[i];
      if (!el.name || el.disabled) continue;
      var type = (el.type || '').toLowerCase();
      if (type === 'submit' || type === 'button') continue;
      if (type === 'checkbox' || type === 'radio') {
        if (!el.checked) continue;
      }
      params.append(el.name, el.value);
    }
    return params;
  }

  function customerFormPostUrl(formEl) {
    var action = formEl.getAttribute('action') || '/contact';
    return action.indexOf('#') >= 0 ? action.split('#')[0] : action;
  }

  function initAjaxCustomerForms() {
    document.addEventListener('submit', function (e) {
      var formEl = e.target;
      if (!formEl || formEl.tagName !== 'FORM' || !formEl.classList.contains('customer-form--ajax')) {
        return;
      }

      e.preventDefault();

      var emailInput =
        formEl.querySelector('input[type="email"][name="contact[email]"]') ||
        formEl.querySelector('input[type="email"]');
      var successEls = formEl.querySelectorAll('.customer-form-success');
      var submitBtn = formEl.querySelector('button[type="submit"]');
      var email = emailInput ? emailInput.value.trim() : '';
      if (!email) return;

      if (submitBtn) submitBtn.disabled = true;

      window.setTimeout(function () {
        var params = customerFormToUrlParams(formEl);
        var postUrl = customerFormPostUrl(formEl);

        fetch(postUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: params.toString(),
          credentials: 'same-origin'
        })
          .then(function (res) {
            if (!res.ok) {
              if (submitBtn) submitBtn.disabled = false;
              return;
            }
            if (emailInput) emailInput.value = '';
            successEls.forEach(function (el) {
              el.hidden = false;
            });
            formEl.querySelectorAll('.js-customer-form-footnote').forEach(function (el) {
              el.hidden = true;
            });
            var row = formEl.querySelector('.waitlist-row');
            if (row) row.style.display = 'none';
            var joinForm = formEl.querySelector('.join-form');
            if (joinForm) joinForm.style.display = 'none';
          })
          .catch(function () {
            if (submitBtn) submitBtn.disabled = false;
          });
      }, 0);
    });
  }

  function stripCustomerPostedQuery() {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('customer_posted')) return;
      url.searchParams.delete('customer_posted');
      url.hash = '';
      var qs = url.searchParams.toString();
      history.replaceState({}, '', url.pathname + (qs ? '?' + qs : '') + url.hash);
    } catch (err) { /* ignore */ }
  }

  // ─── 7. MOBILE NAV (hamburger + drawer) ──────────────────────────────────
  function initMobileNav() {
    var hamburger = document.getElementById('nav-hamburger');
    var drawer    = document.getElementById('nav-drawer');
    var backdrop  = document.getElementById('nav-drawer-backdrop');
    if (!hamburger || !drawer || !backdrop) return;

    function openDrawer() {
      hamburger.setAttribute('aria-expanded', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('nav-drawer--open');
      backdrop.classList.add('nav-drawer-backdrop--visible');
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      hamburger.setAttribute('aria-expanded', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('nav-drawer--open');
      backdrop.classList.remove('nav-drawer-backdrop--visible');
      document.body.style.overflow = '';
    }

    hamburger.addEventListener('click', function () {
      var isOpen = hamburger.getAttribute('aria-expanded') === 'true';
      if (isOpen) { closeDrawer(); } else { openDrawer(); }
    });

    backdrop.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && hamburger.getAttribute('aria-expanded') === 'true') {
        closeDrawer();
        hamburger.focus();
      }
    });

    // Close drawer and nav links on same-page anchor clicks
    drawer.querySelectorAll('a[href*="#"]').forEach(function (link) {
      link.addEventListener('click', closeDrawer);
    });

    // On resize to desktop, reset drawer state
    window.addEventListener('resize', function () {
      if (window.innerWidth > 768) {
        closeDrawer();
      }
    });
  }

  // ─── INIT ──────────────────────────────────────────────────────────────────
  initAjaxCustomerForms();

  document.addEventListener('DOMContentLoaded', function () {
    stripCustomerPostedQuery();
    initWordmark();
    initCart();
    initCountdown();
    initLightbox();
    initQuantitySelectors();
    initMobileNav();
  });

})();
